'use client'
import { ButtonRoundStyle, Form } from '@/components'
import { REGEX_PHONE } from '@/constants/regex'
import { OTPCountDown } from '@/features/auth/components'
import { requestUniversalOtp, verifyUniversalOtp } from '@/features/auth/services'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Stack } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'

interface IRegisterOtpFormProps {
  identifier: string
  identifierType: 'email' | 'phone'
  onNext: (otp: string) => void
  onBack: () => void
}

/**
 * Form component cho việc xác thực OTP trong quá trình đăng ký
 */
export const RegisterOtpForm = ({ identifier, identifierType, onNext, onBack }: IRegisterOtpFormProps) => {
  const [isPending, startTransition] = useTransition()
  const t = useTranslations('UserProfile')
  const methods = useForm()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { handleSubmit } = methods

  const onSubmit = (data: any) => {
    if (!data.otp) {
      setErrorMessage('Vui lòng nhập mã OTP')
      return
    }

    startTransition(async () => {
      try {
        const result = await verifyUniversalOtp(identifier, data.otp)

        if (result.status === 'error') {
          setErrorMessage(result.message || 'Mã OTP không hợp lệ')
          return
        }

        onNext(data.otp)
      } catch (error) {
        console.error('Error verifying OTP:', error)
        setErrorMessage('Có lỗi xảy ra khi xác thực mã OTP')
      }
    })
  }

  const handleOtpComplete = (otpValue: string) => {
    methods.setValue('otp', otpValue)
    onSubmit({ otp: otpValue })
  }

  const handleResendOtp = async () => {
    try {
      const result = await requestUniversalOtp(identifier)

      if (result.status === 'error') {
        setErrorMessage(result.message || 'Có lỗi xảy ra khi gửi lại mã OTP')
        return
      }

      setErrorMessage(null)
    } catch (error) {
      console.error('Error resending OTP:', error)
      setErrorMessage('Có lỗi xảy ra khi gửi lại mã OTP')
    }
  }

  return (
    <Form methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <OTPCountDown
          onSubmit={handleOtpComplete}
          align="center"
          subtitle={t.rich(
            'Vui lòng nhập đoạn mã gồm 6 chữ số vừa gửi để xác minh tài khoản vừa nhập là của bạn',
            {
              goal: REGEX_PHONE.test(identifier) ? `Zalo ${identifier}` : identifier,
              bold: (chunks) => <strong>{chunks}</strong>,
            }
          )}
          onResendOTP={handleResendOtp}
          errorMessage={errorMessage}
        />
        <Stack spacing={2}>
          <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} loading={isPending}>
            {t('Xác minh tài khoản')}
          </ButtonRoundStyle>
          <ButtonRoundStyle variant="outlined" onClick={onBack} disabled={isPending}>
            <ArrowBackIcon /> {t('Quay về')}
          </ButtonRoundStyle>
        </Stack>
      </Stack>
    </Form>
  )
}
